import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { SessionService, User } from '@edl-development/edl-common';
import { Subscription } from 'rxjs';
import { GenericType } from 'src/app/model/base/edl-generic-type.model';
import { EdlComment } from 'src/app/model/comment.model';
import { CommentLookUpTypes } from 'src/app/model/enum/comment-lookup-types.enum';
import { ObservationSummaryDTO } from 'src/app/model/observation/observation-summary-dto.model';
import { ObservationCommentService } from '../../services/observation-comment/observation-comment.service';
import { InitializeEditModePolicy } from '../../enums/initialize-edit-mode-policy.enum';
import { DEFAULT_FILTER_DOMAIN, DEFAULT_TITLE, NEW_COMMENT_ID } from '../../constants/observation-comments.constants';
import { CommentHelpersService } from '../../services/comment-helpers/comment-helpers.service';
import { EdlTooltipIcon } from 'src/app/components/common-components/enum/edl-tooltip-icon.enum';
import { ObservationTypes } from 'src/app/model/enum/observation-types.enum';

@Component({
    selector: 'app-observation-comments',
    templateUrl: './observation-comments.component.html',
    styleUrls: ['./observation-comments.component.scss']
})
export class ObservationCommentsComponent implements OnInit, OnDestroy {
    @Input() title?: string = DEFAULT_TITLE;
    @Input() subtitle?: string = '';
    @Input() help?: string = '';
    @Input() observationSummary?: ObservationSummaryDTO;
    @Input() defaultDomainFilterId?:string = DEFAULT_FILTER_DOMAIN;
    @Input() hideFilter: boolean = false;
    @Input() initEditModePolicy: InitializeEditModePolicy = InitializeEditModePolicy.CLOSE_ALL;
    @Input() isCommentCollapsed: boolean = true;
    @Input() attachmentLabel:string  = "";

    @Input() set observationType(value: GenericType | undefined) {
        const commentObservationTypeValue = this.commentHelperService.adjustObservationType(value?.Type);
        this._observationType = ObservationTypes.GetObservationType(commentObservationTypeValue);
    };

    get observationType(): GenericType | undefined {
        return this._observationType;
    }

    @Input() set isReadOnly(value: boolean) {
        this._isReadOnly = value;
    }

    get isReadOnly(): boolean {
        return this._isReadOnly || (this.observationCommentService?.filters?.isReadOnly ?? false);
    }

    @Output() isCommentCollapsedChange = new EventEmitter<boolean>();

    public isEditMode: {[ commentId: string ]: boolean} = {};

    evalUser?: User | null;
    domainFilterOptions: Array<any> = [];
    domainFilterId: string = DEFAULT_FILTER_DOMAIN;
    comments: EdlComment[] = [];
    filteredComments: EdlComment[] = [];
    isInitialized: boolean = false;
    areCommentsInitialized: boolean = false;

    private _newComment?: EdlComment;

    get newComment(): EdlComment | undefined {
        return this._newComment;
    }

    set newComment(newComment: EdlComment | undefined) {
        const originalCommentId = newComment?.commentID;

        this._newComment = newComment;

        // When the new comment has been saved and closes, it needs to be reset and added to the filtered comment list
        this.updateComments(this.comments, this.isEditMode);

        if (!!originalCommentId && originalCommentId in this.isEditMode) {
            this.isEditMode[originalCommentId] = this.isEditMode[originalCommentId];
        }
    }

    NEW_COMMENT_ID = NEW_COMMENT_ID;
    EdlTooltipIcon = EdlTooltipIcon;

    private _observationType?: GenericType;
    private _isReadOnly: boolean = false;
    private edlCommentsSubscription?: Subscription;
    private isInitializedSubscription?: Subscription;
    private changeEditModeSubscription?: Subscription;

    constructor(
        private observationCommentService: ObservationCommentService,
        private commentHelpersService: CommentHelpersService,
        private sessionService: SessionService,
        private commentHelperService: CommentHelpersService
    ) { }

    ngOnInit(): void {
        this.evalUser = this.sessionService?.user;
        this.domainFilterId = this.defaultDomainFilterId ?? DEFAULT_FILTER_DOMAIN;
        this.domainFilterOptions = CommentLookUpTypes.getOptions(this.observationType);

        this.isInitializedSubscription = this.observationCommentService.isInitialized.subscribe(({ isInitialized, initialComments}) => {
            if (isInitialized) {
                this.edlCommentsSubscription?.unsubscribe();
                this.updateComments(initialComments, {});
                this.edlCommentsSubscription = this.observationCommentService.edlCommentsReplaySubject.subscribe(newComments => this.updateComments(newComments, this.isEditMode));
            }

            this.isInitialized = isInitialized
        });

        this.changeEditModeSubscription = this.observationCommentService.changeEditMode.subscribe(({ commentIds, isEditMode }) => {
            commentIds.forEach(commentId => {
                if (commentId in this.isEditMode) {
                    this.isEditMode[commentId] = isEditMode;
                }

                if (this._newComment?.commentID === commentId) {
                    this.isEditModeChanged_NewComment(false);
                }
            });
        });
    }

    ngOnDestroy(): void {
        this.edlCommentsSubscription?.unsubscribe();
        this.isInitializedSubscription?.unsubscribe();
        this.changeEditModeSubscription?.unsubscribe();
    }

    changeDomainFilter(newFilterId: string) {
        this.domainFilterId = newFilterId;
    }

    private updateComments = (newComments: EdlComment[], oldIsEditMode: {[ commentId: string ]: boolean}) => {
        this.comments = newComments;
        this.filteredComments = this.generateFilteredComments(newComments);
        this.isEditMode = this.generateEditMode(this.filteredComments, oldIsEditMode);
    }

    private generateEditMode = (newComments: EdlComment[], oldIsEditMode: {[ commentId: string ]: boolean}) => {
        const newIsEditMode: {[ commentId: string ]: boolean} = { [NEW_COMMENT_ID]: oldIsEditMode[NEW_COMMENT_ID] ?? this.getDefaultIsEditMode(newComments) };
        newComments.forEach(comment => {
            newIsEditMode[comment.commentID] = oldIsEditMode[comment.commentID] ?? this.getDefaultIsEditMode(newComments, comment);
        });

        return newIsEditMode;
    }

    private getDefaultIsEditMode(newComments: EdlComment[], comment?: EdlComment) {
        switch(this.initEditModePolicy) {
            case InitializeEditModePolicy.OPEN_NEW_COMMENT_IF_NO_COMMENTS:
                return !comment ? (newComments.length === 0) : false;
            case InitializeEditModePolicy.OPEN_ALL:
                return true;
            case InitializeEditModePolicy.CLOSE_ALL:
            default:
                return false;
        }
    }

    private generateFilteredComments(comments: EdlComment[]): EdlComment[] {
        const comments_noNewComment = comments.filter(x => x.commentID !== this.newComment?.commentID);

        return this.commentHelpersService.filterCommentsByDomain(comments_noNewComment, this.domainFilterId);
    }

    toggleCommentDisplay = () => {
        this.setIsCommentCollapsed(!this.isCommentCollapsed);
    }

    trackByFn = (index: number, item: EdlComment) => {
        return item.commentID;
    }

    isEditModeChanged_NewComment(isNewCommentInEditMode: boolean) {
        this.isEditMode[NEW_COMMENT_ID] = isNewCommentInEditMode;

        // When the new comment has been saved and closes, it needs to be reset and added to the filtered comment list
        if (!isNewCommentInEditMode && !!this.newComment) {
            this.newComment = undefined;
        }
    }

    private setIsCommentCollapsed(value: boolean) {
        this.isCommentCollapsed = value;
        this.isCommentCollapsedChange.emit(this.isCommentCollapsed);
    }
}
